import { isMockEnabled } from "@/lib/mock-flags";
import * as mock from "../mocks/fixtures";
import * as real from "./real";
import type {
  Property,
  PublicProperty,
  ListParams,
  PropertyAddress,
  PropertyImage,
  PropertyVideo,
  PropertyVideoStatus,
  PropertyPublication,
  PropertyProposalRef,
  PropertyInspectionRef,
  IntegrationChannel,
  PublicationStatus,
  ProposalHistoryStatus,
  InspectionKind,
  InspectionStatus,
  PropertyShareLink,
  PropertyPartnership,
  PartnerIdentifierKind,
  PartnershipStatus,
  BrokerLookupResult,
  PropertyLookupResult,
  ReceivedPartnership,
  PropertyTransaction,
  PropertyTransactionKind,
  PropertyTransactionStatus,
  PropertyAccessAuthorization,
  AccessAuthorizationStatus,
  AuthorizationVisitor,
  PropertyAvailabilityCheck,
  PropertyReturnCheck,
  PropertyReturnAnswer,
  PropertyReturnAnswerSource,
  PropertyReturnStatus,
  ContractClosureKind,
  ClosureRequestedBy,
  OwnerWelcome,
  SendOwnerWelcomeInput,
  OwnerWelcomeChannels,
  AvailabilityCheckStatus,
  AvailabilityCheckAnswer,
  ViewerRole,
  ExternalPartnerInvite,
  GenerateExternalPartnerInviteInput,
  CoBrokingClient,
  ShareAnalytics,
  ShareVisitRecent,
  ShareVisitTimelinePoint,
  ShareVisitsPage,
  PropertySign,
  PropertySignSize,
  PropertySignOrientation,
  PropertySignTheme,
  PropertySignStatus,
  PropertySignKind,
  CreatePropertySignInput,
  PropertySignHeadline,
} from "../mocks/fixtures";
import type { PropertyStatus } from "../schemas/property";

function pick() {
  return isMockEnabled("properties") ? mock : real;
}

export function listProperties(params?: ListParams): Promise<Property[]> {
  return pick().listProperties(params);
}
export function getProperty(id: string): Promise<Property> {
  return pick().getProperty(id);
}
export function getShareAnalytics(id: string): Promise<ShareAnalytics> {
  return pick().getShareAnalytics(id);
}
export function getShareVisitsPage(
  id: string,
  cursor?: string,
  limit?: number,
): Promise<ShareVisitsPage> {
  return pick().getShareVisitsPage(id, cursor, limit);
}
export function getPropertyByCode(code: string): Promise<PublicProperty> {
  return pick().getPropertyByCode(code);
}
export function createProperty(
  input: Omit<Property, "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt">,
): Promise<Property> {
  return pick().createProperty(input);
}
export function updateProperty(
  id: string,
  input: Partial<
    Omit<Property, "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt">
  >,
): Promise<Property> {
  return pick().updateProperty(id, input);
}
export function deleteProperty(id: string): Promise<{ ok: true }> {
  return pick().deleteProperty(id);
}
export function uploadPropertyImage(
  propertyId: string,
  file: Blob,
  position: number,
): Promise<PropertyImage> {
  return pick().uploadPropertyImage(propertyId, file, position);
}
export function deletePropertyImage(propertyId: string, imageId: string): Promise<void> {
  return pick().deletePropertyImage(propertyId, imageId);
}
export function reorderPropertyImages(propertyId: string, imageIds: string[]): Promise<void> {
  return pick().reorderPropertyImages(propertyId, imageIds);
}
/**
 * Cria o ticket de upload e sobe o vídeo direto ao Vimeo via tus (hub#636).
 * `onProgress` só é reportado no cliente real; o mock resolve direto.
 */
export function createVideoUploadTicket(
  propertyId: string,
  file: File,
  caption?: string,
  onProgress?: (percent: number) => void,
): Promise<PropertyVideo> {
  const client = pick();
  return client === real
    ? real.createVideoUploadTicket(propertyId, file, caption, onProgress)
    : mock.createVideoUploadTicket(propertyId, file, caption);
}
/** Estado atual do vídeo no Vimeo (polling enquanto não estiver `ready`). hub#636. */
export function getPropertyVideoStatus(
  propertyId: string,
  videoId: string,
): Promise<PropertyVideo> {
  return pick().getPropertyVideoStatus(propertyId, videoId);
}
export function deletePropertyVideo(propertyId: string, videoId: string): Promise<void> {
  return pick().deletePropertyVideo(propertyId, videoId);
}
export function reorderPropertyVideos(propertyId: string, videoIds: string[]): Promise<void> {
  return pick().reorderPropertyVideos(propertyId, videoIds);
}
export function changePropertyStatus(id: string, status: PropertyStatus): Promise<Property> {
  return pick().changePropertyStatus(id, status);
}
/** Sobe uma imagem avulsa (presigned) e devolve o mediaAssetId — foto de preview. hub#577. */
export function uploadPreviewImageAsset(file: Blob): Promise<string> {
  return pick().uploadPreviewImageAsset(file);
}
/** Define a foto dedicada de preview do link público. hub#577. */
export function setPreviewImage(id: string, mediaAssetId: string): Promise<Property> {
  return pick().setPreviewImage(id, mediaAssetId);
}
/** Remove a foto de preview do link público. hub#577. */
export function clearPreviewImage(id: string): Promise<Property> {
  return pick().clearPreviewImage(id);
}
/** Histórico de vendas e locações — sempre real (sem mock). hub#483. */
export function getPropertyTransactions(id: string): Promise<PropertyTransaction[]> {
  return real.getPropertyTransactions(id);
}
/** Reabre imóvel vendido/alugado sem contrato — sempre real (sem mock). hub#484. */
export function reopenProperty(id: string): Promise<Property> {
  return real.reopenProperty(id);
}
/** Histórico de check-ins de disponibilidade — sempre real (sem mock). hub#585. */
export function listPropertyReturnChecks(propertyId: string): Promise<PropertyReturnCheck[]> {
  return real.listPropertyReturnChecks(propertyId);
}

/** Registra a resposta que o corretor ouviu do dono por fora (auditada como broker). hub#760. */
export function answerPropertyReturnCheck(
  propertyId: string,
  checkId: string,
  input: { answer: PropertyReturnAnswer; message?: string; expectedAvailableOn?: string },
): Promise<PropertyReturnCheck> {
  return real.answerPropertyReturnCheck(propertyId, checkId, input);
}

/** Reenvia o link da consulta pendente ao proprietário. hub#760. */
export function resendPropertyReturnCheck(
  propertyId: string,
  checkId: string,
): Promise<PropertyReturnCheck> {
  return real.resendPropertyReturnCheck(propertyId, checkId);
}

export function listAvailabilityChecks(propertyId: string): Promise<PropertyAvailabilityCheck[]> {
  return real.listAvailabilityChecks(propertyId);
}
/** Boas-vindas ao proprietário — sempre real (envia e-mail de verdade). hub#707. */
export function sendOwnerWelcome(
  propertyId: string,
  input: SendOwnerWelcomeInput,
): Promise<OwnerWelcome> {
  return real.sendOwnerWelcome(propertyId, input);
}
/** Último envio de boas-vindas do imóvel — sempre real (sem mock). hub#707. */
export function getOwnerWelcome(propertyId: string): Promise<OwnerWelcome | null> {
  return real.getOwnerWelcome(propertyId);
}
/** Pede uma placa — sempre real (o PDF é composto no Worker). hub#709. */
export function requestPropertySign(
  propertyId: string,
  input: CreatePropertySignInput,
): Promise<PropertySign> {
  return real.requestPropertySign(propertyId, input);
}
/** Placas já pedidas para o imóvel — sempre real (sem mock). hub#709. */
export function listPropertySigns(propertyId: string): Promise<PropertySign[]> {
  return real.listPropertySigns(propertyId);
}
/** Liga/desliga o link impresso da placa — sempre real (sem mock). hub#709. */
export function setPropertySignLink(
  propertyId: string,
  requestId: string,
  enabled: boolean,
): Promise<PropertySign> {
  return real.setPropertySignLink(propertyId, requestId, enabled);
}
/** Estado de um pedido de placa — sempre real (sem mock). hub#709. */
export function getPropertySign(propertyId: string, requestId: string): Promise<PropertySign> {
  return real.getPropertySign(propertyId, requestId);
}
/** Reativa o imóvel (status → Active) — sempre real (sem mock). hub#585. */
export function reactivateProperty(id: string): Promise<Property> {
  return real.reactivateProperty(id);
}
/** Melhora a descrição via IA — SEMPRE real (IA é serviço de plataforma; não mockado). hub#505. */
export function enhancePropertyDescription(
  id: string,
  input: { tone?: real.EnhanceTone; length?: real.EnhanceLength; baseText?: string },
): Promise<{ description: string }> {
  return real.enhancePropertyDescription(id, input);
}
export type { EnhanceTone, EnhanceLength } from "./real";

/** Marca interesse de um contato no imóvel → lead + demanda + lembrete — SEMPRE real (ação de domínio). hub#520. */
export function registerPropertyInterest(
  propertyId: string,
  input: real.RegisterInterestInput,
): Promise<real.RegisterInterestResult> {
  return real.registerPropertyInterest(propertyId, input);
}
export type { InterestIntent, RegisterInterestInput, RegisterInterestResult } from "./real";

export function createShareLink(
  propertyId: string,
  input: {
    showAddress: boolean;
    includeVideos?: boolean;
    note?: string;
    expiresInDays?: number;
    maxViews?: number;
    /**
     * Gera o link SEM a identidade do corretor (hub#765). Exige os dados de quem pediu —
     * o servidor devolve 422 `property_share_token.requester_required` sem eles.
     */
    hideBrokerIdentity?: boolean;
    requesterName?: string | null;
    requesterEmail?: string | null;
    requesterPhone?: string | null;
  },
): Promise<PropertyShareLink> {
  return pick().createShareLink(propertyId, input);
}
export function revokeShareLink(propertyId: string, linkId: string): Promise<Property> {
  return pick().revokeShareLink(propertyId, linkId);
}
export function deleteShareLink(propertyId: string, linkId: string): Promise<Property> {
  return pick().deleteShareLink(propertyId, linkId);
}
export function createPartnership(
  propertyId: string,
  input: {
    identifierKind: PartnerIdentifierKind;
    identifier: string;
    partnerPercent: number;
    note?: string;
  },
): Promise<PropertyPartnership> {
  return pick().createPartnership(propertyId, input);
}
export function revokePartnership(propertyId: string, partnershipId: string): Promise<Property> {
  return pick().revokePartnership(propertyId, partnershipId);
}
export function deletePartnership(propertyId: string, partnershipId: string): Promise<Property> {
  return pick().deletePartnership(propertyId, partnershipId);
}
export function generateExternalPartnerInvite(
  propertyId: string,
  input?: GenerateExternalPartnerInviteInput,
): Promise<ExternalPartnerInvite> {
  return pick().generateExternalPartnerInvite(propertyId, input);
}
export function listExternalPartnerInvites(propertyId?: string): Promise<ExternalPartnerInvite[]> {
  return pick().listExternalPartnerInvites(propertyId);
}
export function cancelExternalPartnerInvite(inviteId: string): Promise<{ ok: true }> {
  return pick().cancelExternalPartnerInvite(inviteId);
}
export function indicateCoBrokingClient(
  shareId: string,
  input: { personId: string; role?: string },
): Promise<CoBrokingClient> {
  return pick().indicateCoBrokingClient(shareId, input);
}
export function listCoBrokingClients(shareId: string): Promise<CoBrokingClient[]> {
  return pick().listCoBrokingClients(shareId);
}
export function revokeCoBrokingClient(shareId: string, linkId: string): Promise<{ ok: true }> {
  return pick().revokeCoBrokingClient(shareId, linkId);
}
// ADR-0042: importCoBrokingClient removido (endpoint 410 Gone). B usa o próprio Person.
export function lookupBroker(params: {
  creci?: string;
  uf?: string;
  code?: string;
}): Promise<BrokerLookupResult> {
  return pick().lookupBroker(params);
}
export function lookupProperty(code: string): Promise<PropertyLookupResult> {
  return pick().lookupProperty(code);
}
export function acceptPartnership(propertyId: string, partnershipId: string): Promise<Property> {
  return pick().acceptPartnership(propertyId, partnershipId);
}
export function declinePartnership(propertyId: string, partnershipId: string): Promise<Property> {
  return pick().declinePartnership(propertyId, partnershipId);
}
export function listReceivedPartnerships(status?: string): Promise<ReceivedPartnership[]> {
  return pick().listReceivedPartnerships(status);
}
export function acceptReceivedPartnership(partnershipId: string): Promise<void> {
  return pick().acceptReceivedPartnership(partnershipId);
}
export function declineReceivedPartnership(partnershipId: string): Promise<void> {
  return pick().declineReceivedPartnership(partnershipId);
}

export function createAccessAuthorization(
  propertyId: string,
  input: {
    scheduledAt: string;
    durationMinutes?: number;
    visitors: { nome: string; documento?: string; telefone?: string }[];
    ownerPhone: string;
    note?: string;
    requestedByName?: string;
  },
): Promise<PropertyAccessAuthorization> {
  return pick().createAccessAuthorization(propertyId, input);
}
export function cancelAccessAuthorization(propertyId: string, authId: string): Promise<Property> {
  return pick().cancelAccessAuthorization(propertyId, authId);
}
export function getAccessAuthorizationByToken(token: string) {
  return pick().getAccessAuthorizationByToken(token);
}
export function respondAccessAuthorization(
  token: string,
  response: "authorized" | "denied",
  note?: string,
): Promise<PropertyAccessAuthorization> {
  return pick().respondAccessAuthorization(token, response, note);
}

export type {
  Property,
  ListParams,
  PropertyAddress,
  PropertyImage,
  PropertyVideo,
  PropertyVideoStatus,
  PropertyPublication,
  PropertyProposalRef,
  PropertyInspectionRef,
  IntegrationChannel,
  PublicationStatus,
  ProposalHistoryStatus,
  InspectionKind,
  InspectionStatus,
  PropertyShareLink,
  PropertyPartnership,
  PartnerIdentifierKind,
  PartnershipStatus,
  BrokerLookupResult,
  PropertyLookupResult,
  ReceivedPartnership,
  PropertyTransaction,
  PropertyTransactionKind,
  PropertyTransactionStatus,
  PropertyAccessAuthorization,
  AccessAuthorizationStatus,
  AuthorizationVisitor,
  PropertyAvailabilityCheck,
  AvailabilityCheckStatus,
  AvailabilityCheckAnswer,
  PropertyReturnCheck,
  PropertyReturnAnswer,
  PropertyReturnAnswerSource,
  PropertyReturnStatus,
  ContractClosureKind,
  ClosureRequestedBy,
  OwnerWelcome,
  OwnerWelcomeChannels,
  SendOwnerWelcomeInput,
  ViewerRole,
  ExternalPartnerInvite,
  GenerateExternalPartnerInviteInput,
  CoBrokingClient,
  ShareAnalytics,
  ShareVisitRecent,
  ShareVisitTimelinePoint,
  ShareVisitsPage,
  PropertySign,
  PropertySignSize,
  PropertySignOrientation,
  PropertySignTheme,
  PropertySignStatus,
  PropertySignKind,
  CreatePropertySignInput,
  PropertySignHeadline,
};
export { isSharedProperty, SIGN_HEADLINES } from "../mocks/fixtures";

// Oferta do imóvel à imobiliária (hub#706) — real-only: é um ato com efeito jurídico/comercial
// (a casa passa a ver o ciclo), então não faz sentido simular.
export {
  getPropertyOffer,
  offerPropertyToAgency,
  withdrawPropertyOffer,
  type PropertyOffer,
} from "./real";
export {
  MAX_PROPERTY_VIDEOS,
  MAX_PROPERTY_VIDEO_BYTES,
  MAX_PROPERTY_VIDEO_SECONDS,
} from "../mocks/fixtures";
export {
  PROPERTY_TYPES,
  PROPERTY_OPERATIONS,
  PROPERTY_STATUSES,
  PROPERTY_CATEGORIES,
  PROPERTY_FEATURES,
  type PropertyType,
  type PropertyOperation,
  type PropertyStatus,
  type PropertyCategory,
  type PropertyFeature,
} from "../schemas/property";
